import { css, cx } from '@linaria/core';
import { DOMAttributes, forwardRef, TextareaHTMLAttributes, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import { useRouter } from 'next/router';
import imageCompression from 'browser-image-compression';
import { useCurrentUserId, useIsLoggedIn } from 'modules/Auth/hooks';
import { track } from '@amplitude/analytics-browser';
import { useAuthActions } from 'modules/Auth/AuthContext';
import { useFeatureFlagsLD } from 'modules/FeatureFlagsLD/FeatureFlagsProviderLD';
import { useDeepCompareEffect } from 'react-use';
import { TooltipWrapper } from './TooltipWrapper';
import SvgPlusMd from './Icons/PlusMd';
import SvgXCloseSm from './Icons/XCloseSm';
import SvgArrowUpMd from './Icons/ArrowUpMd';
import { abortError, AbortReason, deployingError, useFetchModelOutput } from '../hooks/utils/useFetchModelOutput/useFetchModelOutput';
import { PlaygroundConfig } from '../types/playgroundConfig';
import { addMessage, resetMessages } from '../store/messageStore';
import { usePanelContext } from './PanelContext';
import SvgStopMd from './Icons/StopMd';
import { constructModelUrlWithVersion } from '../utils/constructModelUrl';
import { getModelUniqueId } from '../utils/getModelUniqueId';
const compressionConfig = {
  maxSizeMB: 1,
  maxWidthOrHeight: 1920,
  useWebWorker: true
};
export interface FilePreview {
  file: File;
  preview: string;
  base64: string;
}
interface ErrorType {
  message?: string;
  cause?: {
    status?: {
      details?: string;
      description?: string;
      reqId?: string;
    };
    reason?: AbortReason;
    prevMessage?: string;
  };
}
export interface ChatInputRef {
  clearInput: () => void;
  setText: (text: string) => void;
  getValue: () => string;
}
export const ChatInput = forwardRef<ChatInputRef>((_, ref) => {
  const panelId = usePanelContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [images, setImages] = useState<FilePreview[]>([]);
  const [textMessage, setTextMessage] = useState('');
  const {
    setFieldValue,
    values
  } = useFormikContext<PlaygroundConfig>();
  const [messageSessionId, setMessageSessionId] = useState<string>();
  const [abortController, setAbortController] = useState<AbortController>();
  const router = useRouter();
  const isStreamEnabled = router.query.streaming !== 'false';
  const modelIsStreamCabable = !['gpt-4o', 'claude-3_5-sonnet', 'pixtral-12b'].includes((values.model?.id as string));
  const isMultimodalModel = values.model?.model_type_id === 'multimodal-to-text';
  const currentUserId = useCurrentUserId();
  const isLoggedIn = useIsLoggedIn();
  const {
    openLoginModal
  } = useAuthActions();
  useEffect(() => {
    // Abort controller aborts the previous request when the model / user changes
    // Need the reason as 'switch' to ensure it doesn't create a user abort error message
    abortController?.abort(('switch' as AbortReason));
    resetMessages(panelId || 'default');
  }, [currentUserId, values.model?.id, panelId]);
  const handleResize: DOMAttributes<HTMLTextAreaElement>['onInput'] = e => {
    e.currentTarget.style.height = '34px';
    e.currentTarget.style.height = `${Math.min(e.currentTarget.scrollHeight, 264)}px`;
  };
  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    for (const file of Array.from(files)) {
      if (file.type.startsWith('image/')) {
        try {
          const compressedFile = await imageCompression(file, compressionConfig);
          const reader = new FileReader();
          reader.addEventListener('load', () => {
            if (reader.result) {
              const base64 = (reader.result as string).split(';base64,')[1];
              setImages(prev => [...prev, {
                file: compressedFile,
                preview: (reader.result as string),
                base64
              }]);
            }
          });
          reader.readAsDataURL(compressedFile);
        } catch (err) {
          console.error('Image compression error:', err);
        }
      }
    }
    e.target.value = '';
  };
  const handleImageDelete = (index: number) => {
    setImages(prev => prev.filter((__, i) => i !== index));
  };
  const {
    fetchOutputForModels,
    streamOutputForModels,
    outputString,
    startTime,
    endTime,
    inTokens,
    outTokens,
    streamOpenAIResponse,
    messagePayloadObject
  } = useFetchModelOutput();
  const {
    openAICompatibleModelsLDFF
  } = useFeatureFlagsLD();
  const openAICompatibleModelList = openAICompatibleModelsLDFF?.modelList ?? [];
  useDeepCompareEffect(() => {
    if (messageSessionId && outputString) {
      addMessage({
        id: messageSessionId,
        role: 'assistant',
        content: outputString,
        startTime,
        endTime,
        inTokens,
        outTokens,
        payload: messagePayloadObject
      }, panelId || 'default');
    }
  }, [outputString, startTime, endTime, messageSessionId, inTokens, outTokens, panelId, messagePayloadObject]);
  const sendMessage = async (message: string, file: FilePreview[]) => {
    const userMessageId = uuidv4();
    const newSessionId = uuidv4();
    const abortControllerObject = new AbortController();
    setAbortController(abortControllerObject);
    addMessage({
      id: userMessageId,
      role: 'user',
      content: message,
      file
    }, panelId || 'default');
    try {
      setMessageSessionId(newSessionId);
      setFieldValue('isOutputLoading', true);
      // TODO: disable streaming when images are present - Need to fix the issue later
      if (modelIsStreamCabable && isStreamEnabled) {
        // TODO: Find out why turning off the loading state is not working for streaming mode
        setFieldValue('isStreaming', true);
        const modelUniqueId = getModelUniqueId(values.model);
        if (openAICompatibleModelList.includes(modelUniqueId)) {
          await streamOpenAIResponse(message, file, abortControllerObject.signal, constructModelUrlWithVersion(values.model, values.modelVersion));
        } else {
          await streamOutputForModels(message, file, abortControllerObject.signal);
        }
      } else {
        await fetchOutputForModels(message, file, abortControllerObject.signal);
      }
    } catch (e) {
      if ((e as Error).message === abortError.message && (e as ErrorType)?.cause?.reason === 'switch') {
        // Aborted because of user switching model or user account
        // Do nothing...
      } else if ((e as Error).message === abortError.message) {
        const prevMessage = (e as ErrorType)?.cause?.prevMessage ? `${(e as ErrorType)?.cause?.prevMessage}

` : '';
        addMessage({
          id: newSessionId,
          role: 'assistant',
          content: prevMessage,
          error: `${prevMessage}\`⚠️ Response cancelled by user\``
        }, panelId || 'default');
      } else if ((e as Error).message === deployingError.message) {
        addMessage({
          id: newSessionId,
          role: 'assistant',
          content: '',
          error: `### ⚠️ Model is deploying
**Message:** ${(e as ErrorType)?.cause?.status?.details || 'Try again after sometime'}`
        }, panelId || 'default');
      } else {
        addMessage({
          id: newSessionId,
          role: 'assistant',
          content: '',
          error: `### 🚫 Error
**Message:** ${(e as ErrorType)?.message}

Request ID: \`${(e as ErrorType)?.cause?.status?.reqId ?? 'N/A'}\`

### 📦 Type
**${(e as ErrorType)?.cause?.status?.description || 'Internal Server Error'}**

### 📝 Details
${(e as ErrorType)?.cause?.status?.details || 'No details available'}`
        }, panelId || 'default');
        setTextMessage(prev => prev || message);
        setImages(prev => prev.length ? prev : file);
      }
    } finally {
      setAbortController(undefined);
      setMessageSessionId(undefined);
      setFieldValue('isOutputLoading', false);
      setFieldValue('isStreaming', false);
    }
  };
  const handleTextMessageChange: TextareaHTMLAttributes<HTMLTextAreaElement>['onChange'] = e => {
    setTextMessage(e.target.value);
  };
  const handleKeyDown: TextareaHTMLAttributes<HTMLTextAreaElement>['onKeyDown'] = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent newline
      if (abortController) {
        return;
      }
      onClickSend();
    }
  };
  const onClickSend = () => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }
    if (abortController) {
      abortController.abort(('user' as AbortReason));
      return;
    }
    const trimmed = textMessage.trim();
    if (trimmed) {
      track('Playground -> Message Send Click', {
        messageLength: trimmed.length,
        imageCount: images.length
      });
      sendMessage(trimmed, images);
      setTextMessage('');
      setImages([]);
    }
  };
  useImperativeHandle(ref, () => ({
    clearInput: () => {
      setTextMessage('');
      setImages([]);
    },
    setText: (text: string) => setTextMessage(text),
    getValue: () => {
      return textMessage;
    }
  }));
  return <div className={chatInputArea}>
      {images.length ? <div className={imagePreviewArea}>
          {images.map((eachImage, index) => {
        const onDeleteImage = () => handleImageDelete(index);
        return <div key={index.toString()}>
                <div className={imagePreviewWrapper}>
                  <img className={cx(uploadImagePreview)} src={eachImage.preview} alt="file-to-upload" />
                  {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events */}
                  <div role="button" onClick={onDeleteImage} className={removeImageButton}>
                    <SvgXCloseSm />
                  </div>
                </div>
              </div>;
      })}
        </div> : null}
      <textarea value={textMessage} onChange={handleTextMessageChange} onInput={handleResize} placeholder="Message AI..." className={chatInput} onKeyDown={handleKeyDown} />
      <div className={chatInputCTA}>
        {isMultimodalModel && !images.length ? <>
            <TooltipWrapper side="right" toolTipText={<div className={uploadInputTooltipStyles}>
                  <span>Choose an image to upload</span>
                  <p>(png, jpeg, webp formats accepted)</p>
                </div>}>
              <button style={{
            marginRight: '0.25rem'
          }} type="button" onClick={handleFileButtonClick} className={fileUploadCTA}>
                <SvgPlusMd />
              </button>
            </TooltipWrapper>
            {/* TODO: Limiting image formats due to backend issue with svg images - should revert once it is fixed */}
            <input className={hiddenInputField} ref={fileInputRef} type="file" accept=".png,.jpg,.jpeg,.webp" onChange={handleFileChange} />
          </> : <div />}
        <button onClick={onClickSend} type="button" className={sendMessageCTA}>
          {abortController ? <SvgStopMd /> : <SvgArrowUpMd />}
        </button>
      </div>
    </div>;
});
const chatInputArea = "odules__Playground__Components__ChatInput_tsx-chatInputArea-c1xvt0gt";
const chatInput = "odules__Playground__Components__ChatInput_tsx-chatInput-c1cf4p9l";
const imagePreviewArea = "odules__Playground__Components__ChatInput_tsx-imagePreviewArea-i11ymza5";
const chatInputCTA = "odules__Playground__Components__ChatInput_tsx-chatInputCTA-c1y4nvde";
const hiddenInputField = "odules__Playground__Components__ChatInput_tsx-hiddenInputField-h6nhx2r";
const fileUploadCTA = "odules__Playground__Components__ChatInput_tsx-fileUploadCTA-f153115e";
const uploadInputTooltipStyles = "odules__Playground__Components__ChatInput_tsx-uploadInputTooltipStyles-u1ng3ov7";
const imagePreviewWrapper = "odules__Playground__Components__ChatInput_tsx-imagePreviewWrapper-i7ixcdl";
const uploadImagePreview = "odules__Playground__Components__ChatInput_tsx-uploadImagePreview-uqu31b1";
const removeImageButton = "odules__Playground__Components__ChatInput_tsx-removeImageButton-r1efp5w1";
const sendMessageCTA = "odules__Playground__Components__ChatInput_tsx-sendMessageCTA-s1f5xzet";

require("./ChatInput.linaria.module.css!=!../../../node_modules/@linaria/webpack-loader/node_modules/@linaria/webpack5-loader/lib/outputCssLoader.js!./ChatInput.tsx");